import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { RiPencilLine } from "react-icons/ri";
import { IdentitySwitchMenu, MemberCircleList } from "@components/IdentitySwitchMenu";
import { isHomeHaze, HAZE_HERO_INK, DAY_NUMBER_FONT } from "./homeSkin";

/* 與訂單完成頁同一隻角色、不同 pose：那裡是慶祝（訂單成功），首頁 hero 用比讚眨眼。
   用 -hero 而不是 -thumbsup：原圖烘了一圈為白底畫的藍灰光暈（半透明像素占全圖 49%），
   放到淡藍霧上，那半張圖的霧會讀成一塊矩形髒污。-hero 是把 alpha ≤ 40 的擴散光剝掉、
   41~90 之間線性拉回避免留下硬邊的版本，角色本身一個像素都沒動。
   原圖 gas-mascot-thumbsup.png 保留著，白底的場景（如訂單完成頁）仍該用它。
   CRA 的圖片模組在不同 loader 設定下可能是 string 或 { default: string }，故取兩者。 */
const mascotModule = require("@styles/images/gas-mascot-hero.png");
const MascotImg = (mascotModule?.default ?? mascotModule) as string;

/**
 * Hero 右下角的吉祥物。
 *
 * 這裡原本是一顆會顯示液位的玻璃鋼瓶，但這組 hero 的液位是寫死的（70%／55%），
 * 從來不反映真實庫存——換成角色沒有任何資訊損失。真正需要液位的是 AI 預測 hero，
 * 那裡的高度與顏色就是「該不該叫瓦斯」的訊號，所以那顆桶留著沒動。
 *
 * 刻意向右出血 8px：它是插畫不是資料圖，貼邊讀起來是「角色站在畫面裡」，
 * 留白置中反而像一張被貼上去的圖。
 */
const HeroMascot = () => <img src={MascotImg} alt="" aria-hidden />;

/**
 * 0 筆訂單 + 已填問卷：左數字／右鋼瓶（對齊正式 AI Hero 構圖）。
 * 「更新問卷」放在數值旁；CTA 在 HomePage 白 sheet。
 * 勿拉高整塊 zone z-index，否則藍底會蓋住下方 CTA。
 */
/**
 * 問卷初估／預測整理中這兩個 hero 的配色。
 * 淡霧版沒有庫存狀態可依（還沒有預測），所以固定用「充足」那組藍霧與深藍墨，
 * 和 AI 預測 hero 的充足狀態長得一樣——同一個 App 不該有兩種淡霧。
 */
const surveyHeroTone = () =>
  isHomeHaze()
    ? {
        bg: "radial-gradient(120% 85% at 50% -10%, #bedcfa 0%, #d3e7fb 34%, #e8f1fc 66%, #f8fafc 100%)",
        strong: HAZE_HERO_INK,
        body: "#3d5a71",
        faint: "#7d94a6",
        shadow: "0 10px 26px rgba(20,60,110,0.12)",
        // 更新問卷那顆小圓鈕。原本整組寫死白色（邊框 rgba(255,255,255,.35)、
        // 底 .16、圖示 #fff），在近白的淡霧上三者全部消失。
        // 大數字不跟 strong 同色——它是這頁的主角，和小標同色就少了一層層次。
        // 品牌藍：比小標的 #3d5a71 亮且彩度高，和 CTA 同家族但沒那麼亮。
        numInk: "#1668c9",
        // 天數下方的螢光筆條。鵝黃是全頁唯一的暖色，和吉祥物頭上那兩道驚嘆號同色系；
        // 橘與紅不能用——那是庫存狀態的語意色（留意／建議補充），會被讀成警示。
        numMark: "#fde68a",
        btnInk: HAZE_HERO_INK,
        btnBorder: "rgba(15,76,129,0.3)",
        btnBg: "rgba(255,255,255,0.66)",
        btnBgHover: "rgba(255,255,255,0.9)",
      }
    : {
        bg: "linear-gradient(180deg, #42A5F5 0%, #1E88E5 100%)",
        strong: "#fff",
        body: "rgba(255,255,255,0.9)",
        faint: "rgba(255,255,255,0.85)",
        shadow: "0 10px 26px rgba(0,40,90,0.22)",
        numInk: "#fff",
        numMark: "rgba(255,255,255,0.28)",
        btnInk: "#fff",
        btnBorder: "rgba(255,255,255,0.35)",
        btnBg: "rgba(255,255,255,0.16)",
        btnBgHover: "rgba(255,255,255,0.26)",
      };

/** YYYY-MM-DD → MM/DD；格式不符時原樣回傳 */
const toMonthDay = (date: string | null): string => {
  if (!date) return "--";
  const parts = date.split("-");
  if (parts.length !== 3) return date;
  return `${parts[1]}/${parts[2]}`;
};

/**
 * 問卷初估 hero。
 *
 * aiForecastEnabled=false（瓦斯行在 PW 關掉 AI 叫桶預測）時，底下那行
 * 「完成一張訂單後會升級智慧預測」要拿掉——這家不會有預測，不能承諾。
 */
export const SurveyCycleEstimateHero = ({
  predictedDays,
  onWhyNoPrediction,
  onUpdateSurvey,
  aiForecastEnabled = true,
}: {
  predictedDays: number;
  onWhyNoPrediction: () => void;
  onUpdateSurvey?: () => void;
  aiForecastEnabled?: boolean;
}) => {
  const HT = surveyHeroTone();
  return (
    <CycleMascotHero
      days={predictedDays}
      lead="大約每"
      unitLine="叫一次瓦斯"
      onUpdateSurvey={onUpdateSurvey}
      footnote={
        aiForecastEnabled ? (
          <button
            type="button"
            onClick={onWhyNoPrediction}
            style={{
              background: "none",
              border: "none",
              color: HT.strong,
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "inherit",
              padding: 0,
              opacity: 0.85,
              textDecoration: "underline",
              textUnderlineOffset: 2,
              textDecorationThickness: 1.2,
            }}>
            完成一張訂單後會升級智慧預測
          </button>
        ) : (
          "依您填寫的用氣習慣估算"
        )
      }
    />
  );
};

/**
 * 平均叫桶 hero：瓦斯行關閉 AI 預測、客戶已有叫桶紀錄時用。
 *
 * 數字是真實訂單的平均間隔（home_stats.avg_order_interval_days），不是問卷估的；
 * 構圖、配色、吉祥物與問卷初估 hero 完全同一套，關閉預測的瓦斯行首頁才不會
 * 掉回另一種長相（舊的深藍漸層 hero 是開啟預測、還在等 AI 學習時的畫面）。
 */
export const AvgCycleHero = ({
  avgDays,
  lastOrderDate,
  onUpdateSurvey,
}: {
  avgDays: number;
  lastOrderDate: string | null;
  onUpdateSurvey?: () => void;
}) => (
  <CycleMascotHero
    days={avgDays}
    lead="平均每"
    unitLine="叫一次瓦斯"
    onUpdateSurvey={onUpdateSurvey}
    footnote={`上次叫桶 ${toMonthDay(lastOrderDate)}`}
  />
);

/**
 * 左數字／右吉祥物的淡霧 hero 骨架（問卷初估、平均叫桶共用）。
 * 「更新問卷」放在數值旁；CTA 在 HomePage 白 sheet。
 * 勿拉高整塊 zone z-index，否則藍底會蓋住下方 CTA。
 */
const CycleMascotHero = ({
  days,
  lead,
  unitLine,
  footnote,
  onUpdateSurvey,
}: {
  days: number;
  /** 數字上方的小標：「大約每」／「平均每」 */
  lead: string;
  /** 數字下方的小標：「叫一次瓦斯」 */
  unitLine: string;
  /** 最底下那行淡字：可以是文字，或一顆說明連結 */
  footnote: ReactNode;
  onUpdateSurvey?: () => void;
}) => {
  const HT = surveyHeroTone();
  const [animDays, setAnimDays] = useState(0);

  useEffect(() => {
    let raf = 0;
    let start: number | undefined;
    const target = Math.max(0, Math.round(days));
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || target <= 0) {
      setAnimDays(target);
      return;
    }
    setAnimDays(0);
    const tick = (t: number) => {
      if (start === undefined) start = t;
      const p = Math.min((t - start) / 700, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setAnimDays(Math.round(target * e));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [days]);

  return (
    <div className="survey-cycle-zone" style={{ position: "relative", background: "#f8fafc" }}>
      <style>{`
        .survey-cycle-zone { position: relative; }
        .survey-cycle-text {
          max-width: calc(100% - 162px);
          position: relative;
          z-index: 2;
        }
        .survey-cycle-fade {
          opacity: 0;
          transform: translateY(8px);
          animation: surveyCycleIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .survey-cycle-fade-1 { animation-delay: 0.05s; }
        .survey-cycle-fade-2 { animation-delay: 0.12s; }
        .survey-cycle-fade-3 { animation-delay: 0.28s; }
        .survey-cycle-fade-4 {
          animation-name: surveyCycleInSoft;
          animation-delay: 0.4s;
        }
        @keyframes surveyCycleIn {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes surveyCycleInSoft {
          to { opacity: 0.65; transform: translateY(0); }
        }
        .survey-cycle-lip {
          margin-top: -88px;
          background: #f8fafc;
          border-radius: 24px 24px 0 0;
          padding: 24px 18px 0;
          position: relative;
          z-index: 1;
          min-height: 28px;
          border: none;
          box-shadow: none;
          pointer-events: none;
        }
        /* 吉祥物 374×346，比原本的鋼瓶矮而寬；right 為負是刻意的出血。 */
        .survey-cycle-mascot {
          position: absolute;
          right: -24px;
          /* 與大數字垂直置中對齊，不是貼底：
             padding 14 + 身分列 40 + margin 32 = 86，小標約 25，
             96px 數字（lineHeight .95）中心落在 ~157；吉祥物 363×345，208 寬 → 198 高，
             故 157 - 99 ≈ 58，再往下微調成 78。
             改圖時若長寬比變了，這三個 top 要跟著重算。
             出血 24px 也是算出來的：吉祥物左緣不能越過文字的右緣，
             見底下 .*-text 的 max-width（讓路寬 ≥ 吉祥物寬 - 出血 - 左內距 22）。 */
          top: 78px;
          width: 208px;
          z-index: 30;
          pointer-events: none;
        }
        .survey-cycle-mascot img { display: block; width: 100%; height: auto; }
        /* 天數下方的螢光筆條。畫在數字後面（z-index -1）而不是加底線，
           因為它要壓過數字下緣一小段——像真的用螢光筆劃過去，不是一條分隔線。
           兩端各往外突出一小段、圓角 3px，避免看起來像色塊填滿。 */
        .survey-cycle-num {
          position: relative;
          display: inline-block;
        }
        .survey-cycle-num::before {
          content: "";
          position: absolute;
          /* 兩端各往外突出 7px：螢光筆是手劃的，起訖不會剛好切齊字面邊界，
             切齊反而像程式畫的底線。 */
          left: -7px;
          right: -7px;
          bottom: 8px;
          height: 22px;
          border-radius: 3px;
          background: var(--survey-num-mark);
          z-index: -1;
        }
        .survey-cycle-update {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          margin: 0 0 6px 4px;
          padding: 0;
          border: 1px solid var(--survey-btn-border);
          border-radius: 999px;
          background: var(--survey-btn-bg);
          color: var(--survey-btn-ink);
          cursor: pointer;
          flex-shrink: 0;
          align-self: flex-end;
          position: relative;
          z-index: 5;
          -webkit-tap-highlight-color: transparent;
          transition: transform 0.15s ease, background 0.15s ease, border-color 0.15s ease;
        }
        .survey-cycle-update:hover {
          background: var(--survey-btn-bg-hover);
        }
        .survey-cycle-update:active {
          transform: scale(0.92);
          background: var(--survey-btn-bg-hover);
        }
        @media (max-width: 390px) {
          .survey-cycle-text { max-width: calc(100% - 150px); }
          .survey-cycle-mascot { width: 190px; right: -20px; top: 85px; }
        }
        @media (max-width: 340px) {
          .survey-cycle-text { max-width: calc(100% - 130px); }
          .survey-cycle-mascot { width: 165px; right: -16px; top: 97px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .survey-cycle-fade {
            opacity: 1;
            transform: none;
            animation: none;
          }
          .survey-cycle-fade-4 { opacity: 0.65; }
        }
      `}</style>

      <div
        style={{
          background: HT.bg,
          padding: "14px 22px 90px",
          color: HT.strong,
          "--survey-num-mark": HT.numMark,
          "--survey-btn-ink": HT.btnInk,
          "--survey-btn-border": HT.btnBorder,
          "--survey-btn-bg": HT.btnBg,
          "--survey-btn-bg-hover": HT.btnBgHover,
        } as CSSProperties}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 40,
            marginBottom: 32,
            position: "relative",
            zIndex: 2,
          }}>
          <IdentitySwitchMenu haze={isHomeHaze()} />
          <MemberCircleList haze={isHomeHaze()} />
        </div>

        <div className="survey-cycle-text">
          <div
            className="survey-cycle-fade survey-cycle-fade-1"
            style={{ fontSize: 15, fontWeight: 600, color: HT.body, marginBottom: 2 }}>
            {lead}
          </div>
          <div
            className="survey-cycle-fade survey-cycle-fade-2"
            style={{ display: "flex", alignItems: "flex-end", gap: 4, marginBottom: 4 }}>
            <span
              className="survey-cycle-num"
              style={{
                fontFamily: DAY_NUMBER_FONT,
                color: HT.numInk,
                fontSize: 96,
                fontWeight: 600,
                lineHeight: 0.95,
                // Fredoka 的字腔本來就比粉圓緊，-2.5 會擠在一起，收到 -1
                letterSpacing: -1,
                fontVariantNumeric: "tabular-nums",
              }}>
              {animDays}
            </span>
            <span style={{ fontSize: 26, fontWeight: 700, lineHeight: 1, paddingBottom: 10, marginLeft: 6 }}>天</span>
            {onUpdateSurvey ? (
              <button
                type="button"
                className="survey-cycle-update"
                aria-label="更新問卷"
                title="更新問卷"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onUpdateSurvey();
                }}>
                <RiPencilLine size={15} aria-hidden />
              </button>
            ) : null}
          </div>
          <div
            className="survey-cycle-fade survey-cycle-fade-3"
            style={{ fontSize: 15, fontWeight: 600, color: HT.body, marginBottom: 5 }}>
            {unitLine}
          </div>
          <div
            className="survey-cycle-fade survey-cycle-fade-4"
            style={{
              fontSize: 13,
              lineHeight: 1.45,
              color: HT.faint,
            }}>
            {footnote}
          </div>
        </div>
      </div>

      <div className="survey-cycle-lip" />

      <div className="survey-cycle-mascot" aria-hidden>
        <HeroMascot />
      </div>
    </div>
  );
};

/**
 * 問卷已收、但 predict_one 尚未寫入／失敗：防呆 Hero。
 * 夜間 daily 仍會補齊；隔天重整即可看到正式數值。
 */
export const SurveyPredictionPendingHero = ({
  onWhyNoPrediction,
}: {
  onWhyNoPrediction: () => void;
}) => {
  const HT = surveyHeroTone();
  return (
    <div className="survey-pending-zone" style={{ position: "relative", background: "#f8fafc" }}>
      <style>{`
        .survey-pending-zone { position: relative; }
        .survey-pending-text {
          max-width: calc(100% - 162px);
          position: relative;
          z-index: 2;
        }
        .survey-pending-fade {
          opacity: 0;
          transform: translateY(8px);
          animation: surveyPendingIn 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .survey-pending-fade-1 { animation-delay: 0.05s; }
        .survey-pending-fade-2 { animation-delay: 0.14s; }
        .survey-pending-fade-3 { animation-delay: 0.28s; }
        @keyframes surveyPendingIn {
          to { opacity: 1; transform: translateY(0); }
        }
        .survey-pending-dots::after {
          content: "";
          display: inline-block;
          width: 1.2em;
          text-align: left;
          animation: surveyPendingDots 1.4s steps(4, end) infinite;
        }
        @keyframes surveyPendingDots {
          0% { content: ""; }
          25% { content: "."; }
          50% { content: ".."; }
          75% { content: "..."; }
        }
        .survey-pending-lip {
          margin-top: -88px;
          background: #f8fafc;
          border-radius: 24px 24px 0 0;
          padding: 24px 18px 0;
          position: relative;
          z-index: 1;
          min-height: 28px;
          border: none;
          box-shadow: none;
          pointer-events: none;
        }
        /* 吉祥物 374×346，比原本的鋼瓶矮而寬；right 為負是刻意的出血。 */
        .survey-pending-mascot {
          position: absolute;
          right: -24px;
          /* 與大數字垂直置中對齊，不是貼底：
             padding 14 + 身分列 40 + margin 32 = 86，小標約 25，
             96px 數字（lineHeight .95）中心落在 ~157；吉祥物 363×345，208 寬 → 198 高，
             故 157 - 99 ≈ 58，再往下微調成 78。
             改圖時若長寬比變了，這三個 top 要跟著重算。
             出血 24px 也是算出來的：吉祥物左緣不能越過文字的右緣，
             見底下 .*-text 的 max-width（讓路寬 ≥ 吉祥物寬 - 出血 - 左內距 22）。 */
          top: 78px;
          width: 208px;
          z-index: 30;
          pointer-events: none;
        }
        .survey-pending-mascot img { display: block; width: 100%; height: auto; }
        @media (max-width: 390px) {
          .survey-pending-text { max-width: calc(100% - 150px); }
          .survey-pending-mascot { width: 190px; right: -20px; top: 85px; }
        }
        @media (max-width: 340px) {
          .survey-pending-text { max-width: calc(100% - 130px); }
          .survey-pending-mascot { width: 165px; right: -16px; top: 97px; }
        }
        @media (prefers-reduced-motion: reduce) {
          .survey-pending-fade {
            opacity: 1;
            transform: none;
            animation: none;
          }
          .survey-pending-dots::after { content: "..."; animation: none; }
        }
      `}</style>

      <div
        style={{
          background: HT.bg,
          padding: "14px 22px 90px",
          color: HT.strong,
          "--survey-num-mark": HT.numMark,
          "--survey-btn-ink": HT.btnInk,
          "--survey-btn-border": HT.btnBorder,
          "--survey-btn-bg": HT.btnBg,
          "--survey-btn-bg-hover": HT.btnBgHover,
        } as CSSProperties}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: 40,
            marginBottom: 32,
            position: "relative",
            zIndex: 2,
          }}>
          <IdentitySwitchMenu haze={isHomeHaze()} />
          <MemberCircleList haze={isHomeHaze()} />
        </div>

        <div className="survey-pending-text">
          <div
            className="survey-pending-fade survey-pending-fade-1"
            style={{ fontSize: 15, fontWeight: 600, color: HT.body, marginBottom: 10 }}>
            已收到您的資料
          </div>
          <div
            className="survey-pending-fade survey-pending-fade-2"
            style={{
              fontSize: 28,
              fontWeight: 800,
              lineHeight: 1.25,
              letterSpacing: -0.4,
              marginBottom: 12,
            }}>
            預測將在不久後準備完成
            <span className="survey-pending-dots" aria-hidden />
          </div>
          <div
            className="survey-pending-fade survey-pending-fade-3"
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: HT.faint,
            }}>
            可先預約叫桶，即將準備啟用智慧預測。
            <button
              type="button"
              onClick={onWhyNoPrediction}
              style={{
                display: "inline",
                background: "none",
                border: "none",
                color: HT.strong,
                fontSize: 13,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "inherit",
                padding: 0,
                marginLeft: 4,
                opacity: 0.9,
                textDecoration: "underline",
                textUnderlineOffset: 2,
                textDecorationThickness: 1.2,
              }}>
              了解更多
            </button>
          </div>
        </div>
      </div>

      <div className="survey-pending-lip" />

      <div className="survey-pending-mascot" aria-hidden>
        <HeroMascot />
      </div>
    </div>
  );
};
